import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// ── Asystent onboardingowy AI OPEN (prawy dolny róg) ──────────────────────────
// Wzorowany na AIAssistant.tsx z mailmanagera: ikona w rogu (przesuwalna na
// telefonie), okno czatu, woła POST /assistant/chat z kontekstem bieżącej zakładki.
// Wiedza o AI OPEN pochodzi z backendu (endpoint /assistant/chat).

const API = 'https://aiopen-api.openkontakt.com/api/v1';
const COLOR = '#7c3aed';

interface Msg { role: 'user' | 'assistant'; content: string; }
interface Props {
  token: string;
  context: string;      // 'conversations' | 'leads' | 'products' | 'settings' | 'onboarding' | ...
  isMobile: boolean;
  triggerOpen?: { question: string; ts: number };
}

// Domyślna pozycja ikony (prawy dolny róg)
// Sugerowane pytania per kontekst (zakladka panelu IdealnySklep)
const SUGGESTIONS: Record<string, string[]> = {
  onboarding: ['Od czego zaczac konfiguracje IdealnySklep?', 'Jak dodac produkty, zeby bot mogl sprzedawac?', 'Jak zainstalowac widget na stronie sklepu?', 'Jak uzupelnic baze wiedzy asystenta?', 'Czy system sam pobierze moje produkty i ceny?'],
  home: ['Od czego zaczac konfiguracje IdealnySklep?', 'Jak dodac produkty, zeby bot mogl sprzedawac?', 'Jak zainstalowac widget na stronie sklepu?', 'Jak uzupelnic baze wiedzy asystenta?', 'Czy system sam pobierze moje produkty i ceny?'],
  conversations: ['Jak przejac rozmowe od Wirtualnego Sprzedawcy?', 'Co robi „+ Wstaw do odpowiedzi"?', 'Jak oddac rozmowe z powrotem botowi („Zwroc AI")?', 'Co oznaczaja wskazniki Rozmowa i Zgloszono Zwrot?', 'Czy widze rozmowy klientow na zywo?', 'W jakim jezyku bot rozmawia z klientem z zagranicy?'],
  leads: ['Jak przekazac zamowienie do realizacji (Apilo/BaseLinker)?', 'Jak bot prowadzi klienta przez proces zamowienia?', 'Jak dzialaja negocjacje ceny i rabaty?', 'Do jakiej wysokosci bot moze udzielic rabatu?', 'Gdzie widze dane klienta i szczegoly zamowienia?', 'Jak otworzyc rozmowe powiazana z zamowieniem?'],
  products: ['Jak zaimportowac produkty (feed XML)?', 'Skad wziac link do feedu z mojego sklepu?', 'Czy produkty aktualizuja sie automatycznie?', 'Jak dodac rynki zagraniczne i waluty?', 'Skad bot bierze kursy walut?', 'Jak dzialaja tlumaczenia produktow na inne jezyki?'],
  settings: ['Co moge ustawic w panelu konfiguracyjnym?', 'Ktore ustawienia sa podstawowe, a ktore zaawansowane?', 'Jak dostosowac wyglad i powitanie asystenta?', 'Jak podpiac moj sklep (PrestaShop / BaseLinker)?'],
  'settings:widget': ['Jak zmienic wyglad asystenta (awatar, kolor, pozycja)?', 'Jak ustawic powitanie i imie asystenta?', 'Jak zainstalowac widget na stronie sklepu?', 'Gdzie znajde kod do wklejenia na strone?', 'Co zrobic, jesli nie mam dostepu do kodu strony?'],
  'settings:discounts': ['Jak ustawic rabaty, ktorych bot moze udzielac?', 'Jak ograniczyc maksymalny rabat?', 'Czy bot moze negocjowac cene z klientem?'],
  'settings:knowledge': ['Jak zasilic asystenta wiedza o moim sklepie?', 'Jak dziala „Zeskanuj moj sklep"?', 'Jak dodac artykul recznie (pytanie i odpowiedz)?', 'Jak dodac strone wiedzy z adresu URL?', 'Do czego sluza „Zasady AI"?'],
  'settings:recommendations': ['Do czego sluza rekomendacje?', 'Jak napisac dobra wskazowke dla asystenta?', 'Czy moge kazac botu polecac konkretne produkty?'],
  'settings:ai': ['Jak skonfigurowac model AI asystenta?', 'Ktorego dostawce i model wybrac?', 'Gdzie wpisac klucze API?', 'Jak dzialaja zasady bezpieczenstwa asystenta?'],
  'settings:channels': ['Jak podpiac moj sklep przez API (PrestaShop)?', 'Jak polaczyc z BaseLinker?', 'Czym roznia sie kanaly: Widget, BaseLinker, API sklepu?'],
  'settings:stats': ['Jak czytac oceny rozmow (Dobrze/Srednio/Slabo)?', 'Co oznaczaja wskazniki: liczba ocen, srednia, procenty?', 'Gdzie zobacze komentarze klientow?', 'Jak wykorzystac slabe oceny do poprawy bota?'],
};

function defaultIconPos() {
  const w = typeof window !== 'undefined' ? window.innerWidth : 400;
  const h = typeof window !== 'undefined' ? window.innerHeight : 800;
  return { x: w - 72, y: h - 88 };
}
function readIconPos() {
  try {
    const raw = localStorage.getItem('aiopen_assistant_pos');
    if (raw) { const p = JSON.parse(raw); if (typeof p.x === 'number' && typeof p.y === 'number') return p; }
  } catch (e) {}
  return defaultIconPos();
}

export default function AIOpenAssistant({ token, context, isMobile, triggerOpen }: Props) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [welcome, setWelcome] = useState('');
  const [iconPos, setIconPos] = useState(readIconPos);

  const listRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number; moved: boolean } | null>(null);

  const ax = () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  // Powitanie per kontekst (pobrane z backendu przy otwarciu)
  useEffect(() => {
    if (!open || welcome || !token) return;
    ax().get(`/assistant/welcome`, { params: { context } })
      .then(r => setWelcome(r.data.welcome || ''))
      .catch(() => setWelcome('Cześć! Jestem asystentem IdealnySklep. W czym pomóc?'));
  }, [open, token, context, welcome]);

  // Otwieranie asystenta z zewnątrz (z gotowym pytaniem)
  useEffect(() => {
    if (!triggerOpen || !triggerOpen.ts) return;
    setOpen(true);
    setInput(triggerOpen.question || '');
  }, [triggerOpen?.ts]);
  // Auto-scroll do najnowszej wiadomości
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, loading, welcome, open]);

  // Utrzymaj ikonę w granicach ekranu przy zmianie rozmiaru okna
  useEffect(() => {
    const onResize = () => {
      setIconPos((p: { x: number; y: number }) => ({
        x: Math.min(Math.max(8, p.x), window.innerWidth - 64),
        y: Math.min(Math.max(8, p.y), window.innerHeight - 64),
      }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const savePos = (p: { x: number; y: number }) => {
    try { localStorage.setItem('aiopen_assistant_pos', JSON.stringify(p)); } catch (e) {}
  };

  // ── Drag ikony (dotyk — telefon) ──
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    dragRef.current = { startX: t.clientX, startY: t.clientY, origX: iconPos.x, origY: iconPos.y, moved: false };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!dragRef.current) return;
    const t = e.touches[0];
    const dx = t.clientX - dragRef.current.startX;
    const dy = t.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) dragRef.current.moved = true;
    const nx = Math.min(Math.max(8, dragRef.current.origX + dx), window.innerWidth - 64);
    const ny = Math.min(Math.max(8, dragRef.current.origY + dy), window.innerHeight - 64);
    setIconPos({ x: nx, y: ny });
  };
  const onTouchEnd = () => {
    if (!dragRef.current) return;
    const moved = dragRef.current.moved;
    dragRef.current = null;
    if (moved) { savePos(iconPos); } else { setOpen(o => !o); }
  };

  // ── Drag ikony (mysz — desktop, opcjonalnie) ──
  const onMouseDown = (e: React.MouseEvent) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: iconPos.x, origY: iconPos.y, moved: false };
    const move = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) dragRef.current.moved = true;
      const nx = Math.min(Math.max(8, dragRef.current.origX + dx), window.innerWidth - 64);
      const ny = Math.min(Math.max(8, dragRef.current.origY + dy), window.innerHeight - 64);
      setIconPos({ x: nx, y: ny });
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      if (!dragRef.current) return;
      const moved = dragRef.current.moved;
      dragRef.current = null;
      if (moved) savePos(iconPos);
      else setOpen(o => !o);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const send = async (forced?: string) => {
    const text = (forced !== undefined ? forced : input).trim();
    if (!text || loading) return;
    const next = [...messages, { role: 'user' as const, content: text }];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const r = await ax().post('/assistant/chat', { messages: next, context });
      setMessages([...next, { role: 'assistant' as const, content: r.data.reply || '…' }]);
    } catch (e) {
      setMessages([...next, { role: 'assistant' as const, content: 'Przepraszam, coś poszło nie tak. Spróbuj ponownie.' }]);
    }
    setLoading(false);
  };

  // Prosty render markdown → zwykły tekst (usuwa ## i ** dla czystszego wyglądu w małym oknie)
  const clean = (s: string) => s
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/^\s*[-*]\s+/gm, '• ');

  // ── style ──
  const panelW = isMobile ? Math.min(window.innerWidth - 24, 360) : 360;
  const panelH = isMobile ? Math.min(window.innerHeight - 140, 520) : 520;

  return (
    <>
      {/* Ikona w rogu */}
      <div
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        style={{
          position: 'fixed', left: iconPos.x, top: iconPos.y, zIndex: 10001,
          width: 56, height: 56, borderRadius: '50%', background: COLOR,
          boxShadow: '0 6px 20px rgba(124,58,237,0.4)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 26, userSelect: 'none', touchAction: 'none',
        }}
        title="Asystent IdealnySklep"
      >
        {open ? '✕' : '🤖'}
      </div>

      {/* Okno czatu */}
      {open && (
        <div style={{
          position: 'fixed', zIndex: 10000,
          right: isMobile ? 12 : Math.max(12, window.innerWidth - iconPos.x - panelW + 56),
          bottom: isMobile ? 84 : Math.max(84, window.innerHeight - iconPos.y),
          width: panelW, height: panelH, maxHeight: '80vh',
          background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #e5e7eb',
        }}>
          {/* Nagłówek */}
          <div style={{ background: COLOR, color: '#fff', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>🤖</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Asystent IdealnySklep</div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>Pomogę Ci uruchomić sprzedawcę</div>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
          </div>

          {/* Lista wiadomości */}
          <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10, background: '#f9fafb' }}>
            {welcome && messages.length === 0 && (
              <div style={{ alignSelf: 'flex-start', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '10px 12px', fontSize: 13, color: '#374151', maxWidth: '85%', lineHeight: 1.45 }}>
                {welcome}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                background: m.role === 'user' ? COLOR : '#fff',
                color: m.role === 'user' ? '#fff' : '#374151',
                border: m.role === 'user' ? 'none' : '1px solid #e5e7eb',
                borderRadius: 12, padding: '10px 12px', fontSize: 13, maxWidth: '85%',
                lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {m.role === 'assistant' ? clean(m.content) : m.content}
              </div>
            ))}
            {loading && (
              <div style={{ alignSelf: 'flex-start', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '10px 14px', fontSize: 13, color: '#9ca3af' }}>
                …
              </div>
            )}
          </div>

          {/* Sugerowane pytania */}
          {!loading && (SUGGESTIONS[context] || SUGGESTIONS[(context||'').split(':')[0]] || SUGGESTIONS.home) && (
            <div style={{ padding: '0 10px 8px', display: 'flex', gap: 6, flexWrap: 'wrap', background: '#fff' }}>
              {(SUGGESTIONS[context] || SUGGESTIONS[(context||'').split(':')[0]] || SUGGESTIONS.home).map(q => (
                <button key={q} onClick={() => send(q)}
                  style={{ padding: '5px 10px', borderRadius: 14, border: '1px solid ' + COLOR, background: '#fff', color: COLOR, fontSize: 11, cursor: 'pointer', lineHeight: 1.3 }}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding: 10, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, background: '#fff' }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Zadaj pytanie…"
              style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid #d1d5db', fontSize: 13, outline: 'none' }}
            />
            <button onClick={() => send()} disabled={loading || !input.trim()}
              style={{ padding: '0 16px', borderRadius: 10, border: 'none', background: COLOR, color: '#fff', fontSize: 18, cursor: loading || !input.trim() ? 'default' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1 }}>
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}
